import { useEffect, useState } from 'react';

import Grid from '@mui/material/Grid';
import { Typography, Box, TextField, Button, Chip, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Stack, IconButton } from '@mui/material';
import { Edit, Delete, Save, Close, Add } from '@mui/icons-material';

import { categoryService } from '../../../services/category-service';
import { AppPage, AppHeader, AppCard, AppListCard, AppListHeader } from '../../../layout/layout';
import { useApiError } from '../../../hooks/useApiError';

interface Category {
    id: number;
    name: string;
    description?: string | null;
}

export default function AdminCategoriesPage() {
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);

    const [newName, setNewName] = useState('');

    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState('');

    const { handleError, ErrorAlert, clearError } = useApiError();

    useEffect(() => {
        const load = async () => {
            try {
                const data = await categoryService.getAll();
                setCategories(data);
            } catch (err) {
                handleError(err, 'Failed to load categories.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [handleError]);

    const handleAddCategory = async () => {
        clearError();
        if (!newName.trim()) return;

        try {
            const created = await categoryService.create({ name: newName.trim() });
            setCategories(prev => [...prev, created]);
            setNewName('');
        } catch (err) {
            handleError(err, 'Failed to create category.');
        }
    };

    const startEdit = (category: Category) => {
        clearError();
        setEditingId(category.id);
        setEditingName(category.name);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingName('');
    };

    const saveEdit = async () => {
        clearError();
        if (editingId == null || !editingName.trim()) return;

        try {
            await categoryService.update({ id: editingId, name: editingName.trim() });

            setCategories(prev =>
                prev.map(c => (c.id === editingId ? { ...c, name: editingName.trim() } : c)),
            );

            cancelEdit();
        } catch (err) {
            handleError(err, 'Failed to update category.');
        }
    };

    const handleDelete = async (id: number) => {
        clearError();
        if (!window.confirm('Delete this category?')) return;

        try {
            await categoryService.delete(id);
            setCategories(prev => prev.filter(c => c.id !== id));
        } catch (err) {
            handleError(err, 'Failed to delete category.');
        }
    };

    return (
        <AppPage>
            <AppHeader
                title="Recipe categories"
                subtitle="Organize your recipes into groups like Breakfast, Desserts, or Vegetarian."
            />

            <Grid container spacing={3}>
                {/* Create card */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <AppCard>
                        <ErrorAlert />

                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            Add category
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Create a new category to help browse and discover recipes.
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Category name"
                                fullWidth
                                size="small"
                                value={newName}
                                onChange={e => setNewName(e.target.value)}
                                placeholder="e.g. Breakfast, Desserts, Vegan"
                            />

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    startIcon={<Add />}
                                    variant="contained"
                                    onClick={handleAddCategory}
                                    disabled={!newName.trim()}
                                >
                                    Add category
                                </Button>
                            </Box>
                        </Box>
                    </AppCard>
                </Grid>

                {/* List card */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <AppListCard>
                        <AppListHeader title="Existing categories" />

                        <Box
                            sx={theme => ({
                                px: 3,
                                py: 1,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: (theme.vars ?? theme).palette.background.default,
                            })}
                        >
                            <Typography variant="body2" color="text.secondary">
                                These categories appear in the public recipe search.
                            </Typography>

                            <Chip
                                size="small"
                                label={`${categories.length} total`}
                                color="primary"
                                variant="outlined"
                            />
                        </Box>

                        <Divider />

                        <TableContainer sx={{ maxHeight: 500 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={2} align="center">
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                                    Loading categories...
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : categories.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={2} align="center">
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                                    No categories yet. Create your first one on the left.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        categories.map(category => {
                                            const isEditing = category.id === editingId;

                                            return (
                                                <TableRow hover key={category.id}>
                                                    <TableCell>
                                                        {isEditing ? (
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                value={editingName}
                                                                onChange={e => setEditingName(e.target.value)}
                                                            />
                                                        ) : (
                                                            <Typography fontWeight={500}>{category.name}</Typography>
                                                        )}
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                            {isEditing ? (
                                                                <>
                                                                    <IconButton size="small" color="primary" onClick={saveEdit}>
                                                                        <Save fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton size="small" onClick={cancelEdit}>
                                                                        <Close fontSize="small" />
                                                                    </IconButton>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() => startEdit(category)}
                                                                    >
                                                                        <Edit fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => handleDelete(category.id)}
                                                                    >
                                                                        <Delete fontSize="small" />
                                                                    </IconButton>
                                                                </>
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AppListCard>
                </Grid>
            </Grid>
        </AppPage>
    );
}
